import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Building, CheckCircle, Clock, Calendar, Eye, MapPin, Phone, Mail, Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JoinedBusinesses() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [businessEvents, setBusinessEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [joiningEvent, setJoiningEvent] = useState(null);

  useEffect(() => {
    fetchJoinedBusinesses();
  }, []);

  const fetchJoinedBusinesses = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Use dedicated endpoint for joined businesses
      const response = await axios.get(`${API}/customers/my-businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error fetching joined businesses:', error);
      setBusinesses([]);
    }
  };

  const handleViewDetails = async (business) => {
    setSelectedBusiness(business);
    setShowDetailsDialog(true);
    setLoadingEvents(true);
    setBusinessEvents([]);

    try {
      const token = localStorage.getItem('token');
      
      // Fetch active events for this business
      const response = await axios.get(`${API}/events/active`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { business_id: business.business_id }
      });
      
      setBusinessEvents(response.data);
    } catch (error) {
      console.error('Error fetching business events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleJoinEvent = async (eventId) => {
    setJoiningEvent(eventId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events/join`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        params: { event_id: eventId }
      });
      
      toast.success('Successfully joined event!');
      
      // Refresh events list
      const response = await axios.get(`${API}/events/active`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { business_id: selectedBusiness.business_id }
      });
      setBusinessEvents(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to join event';
      toast.error(errorMsg);
    } finally {
      setJoiningEvent(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-6 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={() => navigate('/customer/events')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>

        <div className="mb-8">
          <h1 className="font-playfair font-bold text-4xl text-primary mb-2">Joined Businesses</h1>
          <p className="font-outfit text-gray-600">Businesses you are a member of or requested to join</p>
        </div>

        {(businesses || []).length === 0 ? (
          <Card className="p-12 text-center">
            <Building className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="font-outfit text-gray-600">You haven't joined any businesses yet</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(businesses || []).map((business) => (
              <Card key={business.business_id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-playfair font-bold text-2xl text-primary">{business.business_name}</h3>
                  <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{business.business_id}</span>
                </div>
                <p className="font-outfit text-sm text-gray-500 mb-2">{business.business_type}</p>
                <p className="font-outfit text-gray-600 text-sm mb-4">
                  {business.business_description || 'No description available'}
                </p>

                <div className="border-t pt-4 mb-4">
                  <p className="font-outfit text-sm text-gray-600">
                    <strong>Owner:</strong> {business.owner_name}
                  </p>
                  {business.address && (
                    <p className="font-outfit text-sm text-gray-600 mt-1">
                      <strong>Address:</strong> {business.address}
                    </p>
                  )}
                  {business.requested_at && (
                    <p className="font-outfit text-xs text-gray-500 mt-2">
                      Requested: {new Date(business.requested_at).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {business.membership_status === 'approved' ? (
                  <>
                    <div className="flex items-center text-sm text-green-600 font-outfit mb-3">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Member
                    </div>
                    <Button
                      onClick={() => handleViewDetails(business)}
                      className="w-full bg-primary hover:bg-primary/90"
                      size="sm"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Details & Events
                    </Button>
                  </>
                ) : (
                  <div className="flex items-center text-sm text-yellow-600 font-outfit">
                    <Clock className="w-4 h-4 mr-2" />
                    Pending Approval
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Business Details Dialog */}
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl text-primary">
                {selectedBusiness?.business_name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="max-h-[calc(85vh-100px)] overflow-y-auto pr-2">
              {selectedBusiness && (
                <div className="space-y-6">
                  {/* Business Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 font-outfit mb-1">Business Type</p>
                        <p className="font-semibold text-gray-800">{selectedBusiness.business_type}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 font-outfit mb-1">Owner</p>
                        <p className="font-semibold text-gray-800">{selectedBusiness.owner_name}</p>
                      </div>
                      {selectedBusiness.business_address && (
                        <div className="md:col-span-2">
                          <p className="text-sm text-gray-500 font-outfit mb-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Address
                          </p>
                          <p className="text-gray-700">{selectedBusiness.business_address}</p>
                        </div>
                      )}
                      {selectedBusiness.phone_number && (
                        <div>
                          <p className="text-sm text-gray-500 font-outfit mb-1 flex items-center gap-1">
                            <Phone className="w-3 h-3" /> Phone
                          </p>
                          <p className="text-gray-700">{selectedBusiness.phone_number}</p>
                        </div>
                      )}
                      {selectedBusiness.support_email && (
                        <div>
                          <p className="text-sm text-gray-500 font-outfit mb-1 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> Business Email
                          </p>
                          <p className="text-gray-700">{selectedBusiness.support_email}</p>
                        </div>
                      )}
                    </div>
                    {selectedBusiness.business_description && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm text-gray-500 font-outfit mb-1">Description</p>
                        <p className="text-gray-700">{selectedBusiness.business_description}</p>
                      </div>
                    )}
                  </div>

                  {/* Active Events */}
                  <div>
                    <h3 className="font-playfair font-bold text-xl text-primary mb-4 flex items-center gap-2">
                      <Trophy className="w-5 h-5" />
                      Active Events
                    </h3>
                    
                    {loadingEvents ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                        <p className="text-sm text-gray-600 mt-2">Loading events...</p>
                      </div>
                    ) : businessEvents.length === 0 ? (
                      <Card className="p-8 text-center bg-gray-50">
                        <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-gray-600">No active events at the moment</p>
                      </Card>
                    ) : (
                      <div className="space-y-4">
                        {businessEvents.map((event) => (
                          <Card key={event.event_id} className="p-4 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h4 className="font-playfair font-bold text-lg text-primary mb-1">
                                  {event.event_name}
                                </h4>
                                <p className="text-sm text-gray-600 mb-2">
                                  {event.event_description}
                                </p>
                              </div>
                              <Badge className="bg-green-100 text-green-800 ml-2">
                                {event.status}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                              <div className="flex items-center gap-2 text-gray-600">
                                <Calendar className="w-4 h-4" />
                                <span>{new Date(event.start_date).toLocaleDateString()} - {new Date(event.end_date).toLocaleDateString()}</span>
                              </div>
                              {/* Only show coupon if not hidden */}
                              {!event.coupon_value_hidden && (
                                <div className="text-gray-600">
                                  <strong>Points:</strong> {event.coupon_value}
                                </div>
                              )}
                            </div>

                            {event.membership_status === 'approved' ? (
                              <div className="flex items-center text-sm text-green-600 font-semibold">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Already Joined
                              </div>
                            ) : event.membership_status === 'pending' ? (
                              <div className="flex items-center text-sm text-yellow-600 font-semibold">
                                <Clock className="w-4 h-4 mr-2" />
                                Join Request Pending
                              </div>
                            ) : (
                              <Button
                                onClick={() => handleJoinEvent(event.event_id)}
                                disabled={joiningEvent === event.event_id}
                                className="w-full bg-primary hover:bg-primary/90"
                                size="sm"
                              >
                                {joiningEvent === event.event_id ? 'Joining...' : 'Join Event'}
                              </Button>
                            )}
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      onClick={() => setShowDetailsDialog(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
